<template>
  <div v-if="userInfo" class="relative h-screen overflow-hidden text-gray-900 dark:text-gray-100">
    <div v-if="bannerCount > 0" class="fixed inset-x-0 top-0 z-50 flex flex-col">
      <StatusBanner :banner="publicStatusBanner" />
      <StatusBanner :banner="publicMaintenanceBanner" />
    </div>

    <div
      class="absolute inset-x-0 bottom-0 flex min-h-0 w-full overflow-hidden"
      :style="appShellStyle"
    >
      <USidebar
        v-if="!isEmbedded"
        v-model:open="isSidebarOpen"
        collapsible="icon"
        rail
        :menu="{ ui: { content: 'divide-transparent overflow-y-auto overscroll-contain' } }"
        :ui="sidebarUi"
      >
        <template #header="{ state }">
          <div class="flex flex-col gap-4 py-4 w-full">
            <div
              class="flex items-center w-full px-2 lg:mb-1"
              :class="state === 'collapsed' ? 'flex-col gap-2' : 'justify-between'"
            >
              <div
                class="flex items-center gap-2 min-w-0"
                :class="{ 'hidden lg:hidden': state === 'collapsed' }"
              >
                <UButton
                  icon="i-lucide-x"
                  color="neutral"
                  variant="ghost"
                  class="lg:hidden shrink-0"
                  @click="isSidebarOpen = false"
                />
                <img
                  alt="Logo"
                  class="h-6 cursor-pointer transition-transform duration-300 shrink-0"
                  :class="state === 'collapsed' ? 'hidden' : ''"
                  src="https://mbcom-public-assets.s3.eu-central-4.ionoscloud.com/MBCOM_logo_rgb.svg"
                  @click="router.push('/')"
                />
              </div>

              <div
                v-if="
                  hasPermission(userInfo.user.permissions, PERMISSIONS.PORTAL_ADMIN) &&
                  userInfo.environment
                "
                class="h-7 hidden lg:block"
              >
                <UTooltip :text="userInfo.environment.apiUrl" :portal="false">
                  <span
                    :class="
                      userInfo.environment.prod
                        ? 'bg-error-500'
                        : userInfo.environment.activeProfiles.includes('staging')
                          ? 'bg-warning-500'
                          : 'bg-info-500'
                    "
                    class="px-1 py-1 text-[10px] font-bold text-white rounded cursor-help inline-block leading-none uppercase"
                  >
                    {{
                      userInfo.environment.prod
                        ? 'PROD'
                        : userInfo.environment.activeProfiles.includes('staging')
                          ? 'STAG'
                          : 'DEV'
                    }}
                  </span>
                </UTooltip>
              </div>
            </div>

            <div
              class="flex w-full items-center"
              :class="state === 'collapsed' ? 'flex-col' : 'justify-between'"
            >
              <UDropdownMenu
                class="w-full"
                :items="tenantItems"
                :ui="{ content: 'max-h-[70vh] overflow-y-auto' }"
              >
                <UButton
                  v-bind="activeTenant"
                  :trailing-icon="state === 'collapsed' ? undefined : 'i-lucide-chevrons-up-down'"
                  color="neutral"
                  :variant="state === 'collapsed' ? 'ghost' : 'outline'"
                  square
                  :class="state === 'collapsed' ? 'w-10 justify-center align-center' : 'w-full'"
                  class="text-primary h-12 p-2"
                  :label="state === 'collapsed' ? undefined : activeTenant.label"
                  :ui="{
                    trailingIcon: 'text-dimmed ms-auto',
                  }"
                />

                <template #item-leading="{ item }">
                  <UIcon
                    :name="item.icon"
                    :class="item.color ? `text-${item.color as string}-500` : 'text-primary'"
                    class="size-5"
                  />
                </template>

                <template #item-trailing="{ item }">
                  <UIcon v-if="item.active" name="i-lucide-check" class="size-4 text-dimmed" />
                </template>
              </UDropdownMenu>
            </div>
          </div>
        </template>

        <template #default="{ state }">
          <div class="flex flex-col flex-1 pb-3 lg:pt-0 pt-2" @click="closeSidebarOnMobile">
            <UNavigationMenu
              :collapsed="state === 'collapsed'"
              :items="navigationItems"
              orientation="vertical"
              class="flex-1 w-full"
              :ui="{ link: 'p-1.5 overflow-hidden' }"
            />

            <UNavigationMenu
              :collapsed="state === 'collapsed'"
              :items="externalLinks"
              orientation="vertical"
              tooltip
              class="mt-auto w-full"
              :ui="{ link: 'p-1.5 overflow-hidden' }"
            />
          </div>
        </template>

        <template #footer="{ state }">
          <div class="w-full" @click="closeSidebarOnMobile">
            <UNavigationMenu
              class="w-full"
              :collapsed="state === 'collapsed'"
              :items="footerItems"
              orientation="vertical"
              tooltip
              color="neutral"
              :ui="{ link: 'p-1.5 overflow-hidden' }"
            >
              <template #item-leading="{ item }">
                <UIcon
                  :name="item.icon"
                  :class="item.color === 'error' ? 'text-error' : 'text-default'"
                  class="size-5"
                />
              </template>

              <template #item-label="{ item }">
                <span :class="item.color === 'error' ? 'text-error' : undefined">
                  {{ item.label }}
                </span>
              </template>
            </UNavigationMenu>
          </div>
        </template>
      </USidebar>

      <div class="flex-1 flex flex-col min-w-0">
        <header
          v-if="!isEmbedded"
          class="h-(--ui-header-height) shrink-0 flex items-center justify-between px-4 border-b border-default backdrop-blur z-10"
        >
          <div class="flex items-center gap-3 min-w-0">
            <UButton
              icon="i-lucide-panel-left"
              color="neutral"
              variant="ghost"
              aria-label="Toggle sidebar"
              class="shrink-0"
              @click="isSidebarOpen = !isSidebarOpen"
            />

            <UBreadcrumb :items="breadcrumbs" class="hidden sm:flex" />
          </div>

          <div class="flex-1 flex justify-center px-4"></div>

          <div class="flex items-center gap-4 shrink-0">
            <UDropdownMenu
              :items="userDropdownItems"
              :content="{ align: 'end', collisionPadding: 12 }"
              :ui="{ content: 'w-72' }"
            >
              <UButton
                v-bind="user"
                trailing-icon="i-lucide-chevrons-up-down"
                color="neutral"
                variant="ghost"
                class="overflow-hidden"
                :ui="{
                  label: 'hidden sm:block',
                  trailingIcon: 'text-dimmed ms-auto',
                }"
              />
            </UDropdownMenu>
          </div>
        </header>

        <main
          class="flex-1 flex flex-col min-h-0 overflow-y-auto [scrollbar-gutter:stable]"
          :class="isUnpadded ? '' : 'p-4 sm:p-6 lg:p-8'"
        >
          <slot />
        </main>
      </div>
    </div>
    <UModal
      v-model:open="showTenantSyncModal"
      :dismissible="false"
      :close="false"
      title="Mandant gewechselt"
      description="Der Mandant wurde in einem anderen Fenster gewechselt."
    >
      <template #body>
        <p>
          In einem anderen Browser-Tab wurde zu dem Mandanten
          <strong>{{ syncedTenantName }}</strong> gewechselt. Bitte laden Sie die Seite neu, um
          fortzufahren.
        </p>
      </template>

      <template #footer>
        <UButton label="Seite neu laden" color="primary" block @click="reloadPage" />
      </template>
    </UModal>
    <ConfirmationModal
      :open="!!pendingTenantSwitch"
      title="Kunden wechseln"
      confirm-label="Kunden wechseln"
      confirm-color="warning"
      :loading="authStore.loading"
      data-test="tenant-switch-confirmation"
      @update:open="(open: boolean) => !open && authStore.cancelTenantSwitch()"
      @confirm="confirmTenantSwitch"
    >
      <template #content>
        <div v-if="pendingTenantSwitch" class="space-y-3 text-sm">
          <p>
            Sie wechseln zum Mandanten
            <strong>{{ pendingTenantSwitch.tenantName }}</strong> des Kunden
            <strong>{{ pendingTenantSwitchCustomerLabel }}</strong>.
          </p>
          <p class="text-muted">
            Der Wechsel zu einem anderen Kunden wird protokolliert und ist für die
            Administratoren dieses Kunden im Zugriffsprotokoll einsehbar.
          </p>
        </div>
      </template>
    </ConfirmationModal>
  </div>
  <div
    v-else
    class="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 flex flex-col"
  >
    <div v-if="bannerCount > 0" class="fixed inset-x-0 top-0 z-50 flex flex-col">
      <StatusBanner :banner="publicStatusBanner" />
      <StatusBanner :banner="publicMaintenanceBanner" />
    </div>
    <main class="flex-1">
      <slot />
    </main>
    <footer
      v-if="showRecoveryLink"
      class="py-4 text-center text-xs text-gray-500 dark:text-gray-400"
    >
      <RouterLink
        to="/recovery"
        class="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors underline-offset-4 hover:underline"
        data-test="unauthenticated-recovery-link"
      >
        Zugang wiederherstellen
      </RouterLink>
    </footer>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import axios from 'axios'
import type { NavigationMenuItem, DropdownMenuItem, BreadcrumbItem } from '@nuxt/ui'

import { IconMicrotech } from '@/icons/icon'
import StatusBanner from '@/components/StatusBanner.vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import type { TenantContext } from '@/types'
import {
  buildPublicMaintenanceBanner,
  buildPublicStatusBanner,
  normalizePublicStatusResponse,
  type PublicMaintenanceBanner,
  type PublicStatusBanner,
} from '@/utils/public-status'
import { redirectToLogin } from '@/utils/auth-redirect'
import { isPublicAppPath } from '@/utils/public-routes'
import { PERMISSIONS, hasPermission } from '@/utils/roles'
import { getDisplayableGroupItems } from '@/utils/user-dropdown'
import { DOCS_HOME_URL, INTERNAL_DOCS_URL } from '@/utils/docs-links'
import { useTheme } from '@/composables/useTheme'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()
const { userInfo } = storeToRefs(authStore)
const breakpoints = useBreakpoints(breakpointsTailwind)
const isLgScreen = breakpoints.greaterOrEqual('lg')
const { theme, setTheme } = useTheme()
const isSidebarOpen = ref(false)
const publicStatusBanner = ref<PublicStatusBanner | null>(null)
const publicMaintenanceBanner = ref<PublicMaintenanceBanner | null>(null)
const showTenantSyncModal = ref(false)
const syncedTenantName = ref('')
let tenantChannel: BroadcastChannel | undefined
let statusRefreshTimer: number | undefined
let sessionRefreshTimer: number | undefined

const SESSION_REFRESH_INTERVAL_MS = 30 * 60 * 1000

const isUnpadded = computed(() => route.meta.unpadded)
const isEmbedded = computed(() => route.meta.embedded)
// Öffentliche Routen (z. B. Kunden-Onboarding) dürfen keine Anmeldung erzwingen.
const isPublicRoute = computed(
  () => route.meta.requiresAuth === false || isPublicAppPath(route.path),
)
const showRecoveryLink = computed(() => !isPublicAppPath(route.path))
const bannerCount = computed(
  () => Number(Boolean(publicStatusBanner.value)) + Number(Boolean(publicMaintenanceBanner.value)),
)
const appShellStyle = computed(() =>
  bannerCount.value > 0
    ? {
        '--app-status-banner-offset': `${bannerCount.value * 2}rem`,
        top: 'var(--app-status-banner-offset)',
      }
    : {
        top: '0',
      },
)
const sidebarUi = computed(() => ({
  container:
    'absolute inset-y-0 z-10 hidden h-full w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear lg:flex',
  inner: 'divide-transparent',
  // Mobile renders header/body/footer inside a slideover: let the whole panel scroll instead of
  // squeezing the body, otherwise nav items are clipped on short (landscape) screens.
  header: isLgScreen.value ? undefined : 'shrink-0',
  body: isLgScreen.value ? 'py-0' : 'py-0 flex-none overflow-visible',
  footer: isLgScreen.value
    ? 'border-t border-default'
    : 'border-t border-default shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))]',
  rail: 'absolute inset-y-0 z-20 hidden w-4 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-px lg:flex hover:after:bg-(--ui-border-accented) after:transition-colors',
}))

const refreshPublicStatus = async () => {
  try {
    const response = await fetch('https://status.mbcom.cloud/api/status', {
      cache: 'no-store',
    })

    if (!response.ok) {
      throw new Error(`Status API request failed with ${response.status}`)
    }

    const statusResponse = normalizePublicStatusResponse(await response.json())
    publicStatusBanner.value = buildPublicStatusBanner(statusResponse)
    publicMaintenanceBanner.value = buildPublicMaintenanceBanner(statusResponse)
  } catch (error) {
    console.error('Failed to fetch public status banner', error)
    publicStatusBanner.value = null
    publicMaintenanceBanner.value = null
  }
}

const appConfig = useAppConfig()

// Define a mapping of customer numbers to primary colors
const getTenantColor = (primaryColor?: string) => {
  return primaryColor || 'blue'
}

watch(
  () => userInfo.value?.activeTenant?.primaryColor,
  (color) => {
    appConfig.ui.colors.primary = getTenantColor(color)
  },
  { immediate: true },
)

watch(
  () => route.fullPath,
  () => {
    if (!isLgScreen.value) {
      isSidebarOpen.value = false
    }
  },
)

watch(
  isLgScreen,
  (isLg) => {
    isSidebarOpen.value = isLg
  },
  { immediate: true },
)

const activeTenant = computed(() => {
  if (userInfo.value?.activeTenant) {
    return {
      label: `${userInfo.value.activeTenant.customerNo} - ${userInfo.value.activeTenant.tenantName}`,
      icon: 'i-mdi-circle',
      color: (userInfo.value.activeTenant.primaryColor as DropdownMenuItem['color']) || 'blue',
      ui: {
        leadingIcon: 'size-5',
      },
    }
  }
  return {
    label: 'Select Tenant',
    icon: 'i-mdi-circle',
    color: 'neutral' as DropdownMenuItem['color'],
    ui: {
      leadingIcon: 'text-gray-400 size-5',
    },
  }
})

// All accessible tenants grouped by customer, the active customer first. Switching into another
// customer asks for confirmation (see authStore.requestTenantSwitch).
const tenantItems = computed<DropdownMenuItem[][]>(() => {
  const allowed = userInfo.value?.allowedTenants
  if (!allowed) return []

  const active = userInfo.value?.activeTenant
  const byCustomer = new Map<string, TenantContext[]>()
  for (const t of allowed) {
    byCustomer.set(t.customerNo, [...(byCustomer.get(t.customerNo) ?? []), t])
  }
  const customerNos = [...byCustomer.keys()].sort((a, b) => {
    if (a === active?.customerNo) return -1
    if (b === active?.customerNo) return 1
    return a.localeCompare(b, undefined, { numeric: true })
  })

  return customerNos.map((customerNo) => {
    const tenants = byCustomer.get(customerNo)!
    const customerName = tenants.find((t) => t.customerName)?.customerName
    return [
      { type: 'label' as const, label: customerName ? `${customerNo} - ${customerName}` : customerNo },
      ...tenants
        .slice()
        .sort((a, b) => a.tenantName.localeCompare(b.tenantName))
        .map((t) => ({
          label: t.tenantName,
          icon: 'i-mdi-circle',
          color: (t.primaryColor as DropdownMenuItem['color']) || 'blue',
          active: t.customerNo === active?.customerNo && t.tenantName === active?.tenantName,
          onSelect: () => {
            authStore.requestTenantSwitch(t.customerNo, t.tenantName)
          },
        })),
    ]
  })
})

const pendingTenantSwitch = computed(() => authStore.pendingTenantSwitch)
const pendingTenantSwitchCustomerLabel = computed(() => {
  const pending = pendingTenantSwitch.value
  if (!pending) return ''
  return pending.customerName ? `${pending.customerNo} - ${pending.customerName}` : pending.customerNo
})

const confirmTenantSwitch = async () => {
  try {
    await authStore.confirmTenantSwitch()
  } catch {
    toast.add({
      title: 'Kundenwechsel fehlgeschlagen',
      description: 'Der Wechsel zum ausgewählten Mandanten war nicht möglich.',
      color: 'error',
    })
  }
}

const navigationItems = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = []

  if (hasPermission(userInfo.value?.user.permissions, PERMISSIONS.TENANT_ACCESS)) {
    items.push({
      label: 'Dashboard',
      icon: 'i-lucide:layout-dashboard',
      to: '/',
    })
    items.push({
      label: 'Aufgaben',
      icon: 'i-lucide:list-todo',
      to: '/tasks',
    })
  }

  const microtechChildren: NavigationMenuItem[] = []
  if (hasPermission(userInfo.value?.user.permissions, PERMISSIONS.OPENSEARCH_READ)) {
    microtechChildren.push({
      label: 'Suche',
      to: '/search',
    })
  }

  if (hasPermission(userInfo.value?.user.permissions, PERMISSIONS.MICROTECH_DATA_READ)) {
    microtechChildren.push(
      {
        label: 'Adressen',
        to: '/microtech-hub/addresses',
      },
      {
        label: 'Artikel',
        to: '/microtech-hub/products',
      },
      {
        label: 'Projekte',
        to: '/microtech-hub/projects',
      },
      {
        label: 'Vorgänge',
        to: '/microtech-hub/transactions',
      },
      {
        label: 'Masterdashboard',
        to: '/microtech-hub/analysis',
      },
    )
  }

  if (microtechChildren.length > 0) {
    items.push({
      label: 'microtech Daten',
      icon: IconMicrotech,
      children: microtechChildren,
    })
  }

  return items
})

const externalLinks = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = []

  items.push({
    label: 'Status',
    icon: 'i-lucide:signal',
    to: 'https://status.mbcom.cloud',
    target: '_blank',
  })

  items.push({
    label: 'Dokumentation',
    icon: 'i-lucide:book-open',
    to: DOCS_HOME_URL,
    target: '_blank',
  })

  if (hasPermission(userInfo.value?.user.permissions, PERMISSIONS.PORTAL_ADMIN)) {
    items.push({
      label: 'Interne Docs',
      icon: 'i-lucide:book-open',
      to: INTERNAL_DOCS_URL,
      target: '_blank',
    })
  }

  return items
})

const footerItems = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = []

  items.push({
    label: 'Notfall melden',
    icon: 'i-lucide-life-buoy',
    to: '/emergency',
    color: 'error',
    class: 'text-error',
  })

  items.push({
    label: 'Support & Feedback',
    icon: 'i-lucide-help-circle',
    to: '/feedback',
  })

  if (hasPermission(userInfo.value?.user.permissions, PERMISSIONS.PORTAL_ADMIN)) {
    items.push({
      label: 'Kunden',
      icon: 'i-lucide:users',
      to: '/customers',
    })
    items.push({
      label: 'OIDC Clients',
      icon: 'i-lucide:key',
      to: '/oidc-clients',
    })
  }

  if (
    userInfo.value?.activeTenant &&
    hasPermission(userInfo.value?.user.permissions, PERMISSIONS.SETTINGS_READ)
  ) {
    items.push({
      label: 'Administration',
      icon: 'i-lucide:settings',
      to: '/admin/general',
    })
  }

  if (hasPermission(userInfo.value?.user.permissions, PERMISSIONS.CUSTOMER_SETTINGS_READ)) {
    items.push({
      label: 'Kunden-Administration',
      icon: 'i-lucide:building-2',
      to: '/customer-admin/general',
    })
  }

  return items
})

const user = computed(() => {
  const fullName = userInfo.value?.user.fullName || ''

  return {
    label: fullName,
    avatar: {
      alt: fullName,
      size: 'sm' as const,
    },
  }
})

const displayableGroups = computed(() =>
  getDisplayableGroupItems(
    userInfo.value?.user.groupDetails,
    userInfo.value?.user.groupMemberships ?? userInfo.value?.user.groups,
  ),
)

const copyGroupToClipboard = async (group: string, event: Event) => {
  event.preventDefault()

  try {
    if (!navigator.clipboard) {
      throw new Error('Clipboard API unavailable')
    }
    await navigator.clipboard.writeText(group)
    toast.add({
      title: 'Gruppe kopiert',
      description: 'Die Gruppen-ID wurde in die Zwischenablage kopiert.',
      color: 'success',
    })
  } catch (error) {
    console.error('Failed to copy group', error)
    toast.add({
      title: 'Kopieren fehlgeschlagen',
      description: 'Die Gruppen-ID konnte nicht kopiert werden.',
      color: 'danger',
    })
  }
}

// "Mein Konto" gibt es nur für lokale Benutzer: /me/local antwortet für alle anderen mit 403.
const hasLocalAccount = ref(false)
const loadLocalAccount = async () => {
  try {
    await axios.get('/api/registry/v1/me/local')
    hasLocalAccount.value = true
  } catch {
    hasLocalAccount.value = false
  }
}

const userDropdownItems = computed<DropdownMenuItem[][]>(() => {
  const items: DropdownMenuItem[][] = [
    [
      {
        label: userInfo.value?.user.email || '',
        icon: 'i-lucide:user',
        disabled: true,
      },
    ],
  ]

  if (displayableGroups.value.length > 0) {
    items.push([
      {
        label: 'Gruppen',
        icon: 'i-lucide:users',
        children: displayableGroups.value.map((group) => ({
          label: group.label,
          icon: 'i-lucide:users',
          onSelect: (event: Event) => copyGroupToClipboard(group.id, event),
          class: 'w-full whitespace-normal break-words',
        })),
      },
    ])
  }

  if (hasLocalAccount.value) {
    items.push([
      {
        label: 'Mein Konto',
        icon: 'i-lucide:user-cog',
        to: '/account',
      },
    ])
  }

  items.push(
    [
      {
        label: 'Appearance',
        icon: 'i-lucide:sun-moon',
        children: [
          {
            label: 'Light',
            icon: 'i-lucide-sun',
            type: 'checkbox',
            checked: theme.value === 'light',
            onSelect(e: Event) {
              e.preventDefault()
              setTheme('light')
            },
          },
          {
            label: 'Dark',
            icon: 'i-lucide-moon',
            type: 'checkbox',
            checked: theme.value === 'dark',
            onSelect(e: Event) {
              e.preventDefault()
              setTheme('dark')
            },
          },
          {
            label: 'System',
            icon: 'i-lucide-monitor',
            type: 'checkbox',
            checked: theme.value === 'auto',
            onSelect(e: Event) {
              e.preventDefault()
              setTheme('auto')
            },
          },
        ],
      },
    ],
    [
      {
        label: 'Logout',
        icon: 'i-heroicons-arrow-right-on-rectangle',
        onSelect: () => {
          logout()
        },
      },
    ],
  )

  return items
})

const startSessionRefreshTimer = () => {
  if (sessionRefreshTimer) {
    return
  }
  sessionRefreshTimer = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      void authStore.refreshSession()
    }
  }, SESSION_REFRESH_INTERVAL_MS)
}

const stopSessionRefreshTimer = () => {
  if (sessionRefreshTimer) {
    window.clearInterval(sessionRefreshTimer)
    sessionRefreshTimer = undefined
  }
}

const ensureUserInfoLoaded = async () => {
  try {
    if (!userInfo.value) {
      await authStore.fetchUserInfo()
    }
  } catch (error: unknown) {
    console.error('Failed to fetch user info', error)
    if (
      axios.isAxiosError(error) &&
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      redirectToLogin()
    }
  }
}

watch(isPublicRoute, (isPublic) => {
  if (isPublic) {
    stopSessionRefreshTimer()
    return
  }

  startSessionRefreshTimer()
  void ensureUserInfoLoaded()
})

onMounted(async () => {
  await refreshPublicStatus()
  statusRefreshTimer = window.setInterval(() => {
    void refreshPublicStatus()
  }, 60_000)

  tenantChannel = new BroadcastChannel('auth-events')
  tenantChannel.onmessage = (event) => {
    if (event.data.type === 'TENANT_SWITCHED') {
      if (
        event.data.customerNo !== userInfo.value?.activeTenant?.customerNo ||
        event.data.tenantName !== userInfo.value?.activeTenant?.tenantName
      ) {
        syncedTenantName.value = event.data.tenantName
        showTenantSyncModal.value = true
      }
    }
  }

  if (isPublicRoute.value) {
    return
  }

  startSessionRefreshTimer()
  await ensureUserInfoLoaded()
  void loadLocalAccount()
})

onBeforeUnmount(() => {
  if (statusRefreshTimer) {
    window.clearInterval(statusRefreshTimer)
  }
  stopSessionRefreshTimer()
  if (tenantChannel) {
    tenantChannel.close()
  }
})

const segmentLabels: Record<string, string> = {
  admin: 'Administration',
  'customer-admin': 'Kunden-Administration',
  'microtech-hub': 'microtech Daten',
  tasks: 'Aufgaben',
  customers: 'Kunden',
  'oidc-clients': 'OIDC Clients',
  projects: 'Projekte',
  addresses: 'Adressen',
  products: 'Artikel',
  transactions: 'Vorgänge',
  statistics: 'Statistik',
  general: 'Allgemein',
  workflow: 'Workflow',
  bi: 'Business Intelligence',
  tenants: 'Mandanten',
  elo: 'ELO',
}

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  const path = router.currentRoute.value.path
  const items: BreadcrumbItem[] = [
    {
      label: 'Dashboard',
      icon: 'i-lucide-layout-dashboard',
      to: '/',
    },
  ]

  if (path === '/') {
    return items
  }

  const segments = path.split('/').filter(Boolean)
  let currentPath = ''

  segments.forEach((segment) => {
    currentPath += `/${segment}`

    // Try to find label in sidebar items
    let label = findSidebarLabel([...navigationItems.value, ...footerItems.value], currentPath)

    // Fallback to segment mapping or detail labels
    if (!label) {
      if (currentPath.includes('/tasks/') && segment !== 'tasks') {
        label = 'Aufgaben-Details'
      } else if (currentPath.includes('/customers/') && segment !== 'customers') {
        label = 'Kunden-Details'
      } else if (currentPath.includes('/microtech-hub/addresses/') && segment !== 'addresses') {
        label = 'Adressen-Details'
      } else if (currentPath.includes('/microtech-hub/products/') && segment !== 'products') {
        label = 'Artikel-Details'
      } else if (currentPath.includes('/microtech-hub/projects/') && segment !== 'projects') {
        label = 'Projekt-Details'
      } else if (
        currentPath.includes('/microtech-hub/transactions/') &&
        segment !== 'transactions'
      ) {
        label = 'Vorgangs-Details'
      } else if (currentPath.includes('/admin/workflow/') && segment !== 'workflow') {
        if (segment === 'new') {
          label = 'Workflow erstellen'
        } else if (segment === 'edit') {
          label = 'Bearbeiten'
        } else {
          label = segment
        }
      } else {
        label = segmentLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1)
      }
    }

    const resolved = router.resolve(currentPath)
    const exists = resolved.matched.length > 0 && resolved.name !== 'not-found'
    items.push({
      label: label as string,
      to: exists ? currentPath : undefined,
    })
  })

  return items
})

const logout = () => {
  authStore.logout()
}

const reloadPage = () => {
  window.location.reload()
}

const closeSidebarOnMobile = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!isLgScreen.value && target.closest('a')) {
    isSidebarOpen.value = false
  }
}

const findSidebarLabel = (items: NavigationMenuItem[], path: string): string | null => {
  for (const item of items) {
    if (item.to === path) {
      return item.label as string
    }
    if (item.children) {
      const childLabel = findSidebarLabel(item.children as NavigationMenuItem[], path)
      if (childLabel) return childLabel
    }
  }
  return null
}
</script>
